import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Returns the user's preferred crew column order for a project. Persists in
 * localStorage so each producer arranges columns the way they like without
 * affecting other team members.
 *
 * Behavior:
 *   - Unknown crew (newly added) append at the end.
 *   - Deleted crew silently drop out.
 *   - When `crewMembers` changes (add/remove), the saved order is reconciled
 *     so we never end up with stale ids.
 */
export function useCrewOrder(projectId, crewMembers) {
  const storageKey = projectId ? `psconsole.crew-order.${projectId}` : null;

  const [order, setOrder] = useState(() => {
    if (!storageKey) return [];
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.warn("crew-order parse failed", e);
      return [];
    }
  });

  // Re-hydrate when the projectId changes (switching projects).
  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = localStorage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      setOrder(Array.isArray(parsed) ? parsed : []);
    } catch (e) {
      console.warn("crew-order rehydrate failed", e);
      setOrder([]);
    }
  }, [storageKey]);

  // Persist whenever the order changes.
  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(order));
    } catch (e) {
      // localStorage full / private mode — order works in-memory only.
      console.warn("crew-order persist failed", e);
    }
  }, [storageKey, order]);

  // Reconcile the saved order against the current crew list. Stable: only
  // returns a new array when the resolved order actually changes (so memo
  // consumers don't re-render needlessly).
  const orderedCrew = useMemo(() => {
    if (!crewMembers || crewMembers.length === 0) return [];
    const byId = new Map(crewMembers.map((c) => [c.id, c]));
    const seen = new Set();
    const out = [];
    for (const id of order) {
      const c = byId.get(id);
      if (c && !seen.has(id)) {
        out.push(c);
        seen.add(id);
      }
    }
    // Append any new crew that don't appear in the saved order yet, in the
    // server's natural order.
    for (const c of crewMembers) {
      if (!seen.has(c.id)) out.push(c);
    }
    return out;
  }, [crewMembers, order]);

  /**
   * Move the crew member currently at `fromIdx` so it sits at `toIdx` (using
   * the **resolved order** indices, not the raw `order` indices). Triggers a
   * persist via the effect above.
   */
  const moveCrew = useCallback((fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    setOrder(() => {
      // Always operate on the fresh resolved order so newly-added crew that
      // appended automatically can also be reordered immediately, before the
      // user has explicitly saved any order yet.
      const ids = orderedCrew.map((c) => c.id);
      if (fromIdx < 0 || fromIdx >= ids.length) return ids;
      const clamped = Math.max(0, Math.min(toIdx, ids.length));
      const [moved] = ids.splice(fromIdx, 1);
      // After the splice, indices >= fromIdx shift by 1. Adjust insertion.
      const adjusted = clamped > fromIdx ? clamped - 1 : clamped;
      ids.splice(adjusted, 0, moved);
      return ids;
    });
  }, [orderedCrew]);

  return { orderedCrew, moveCrew };
}
